import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useResendVerifyEmail as useResendVerifyEmailMutation,
    useVerifyEmail as useVerifyEmailMutation,
} from '@/api/mutations/useAuthMutations';
import { Loading } from '@/components/Loading/Loading';
import { LoadingScreen } from '@/components/LoadingScreen/LoadingScreen';
import { Input } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { paths } from '@/constants';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import { isApiError, type RegistrationTypes } from '@/types';
import { validateRegistartion } from '@/utils/validators';

export const initialValues: Partial<RegistrationTypes> = {
    email: '',
};

export const VerifyEmail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const validate = validateRegistartion(t);
    const [searchParams, _setSearchParams] = useSearchParams();
    const verify_url = searchParams.get('verify_url') || '';
    const url = new URL(verify_url);
    const id = +(url.searchParams.get('id') || '');
    const hash = url.searchParams.get('hash');
    const expires = url.searchParams.get('expires');
    const signature = url.searchParams.get('signature');
    const { mutateAsync: verifyEmail, status } = useVerifyEmailMutation();
    const { mutateAsync: resendVerifyEmail, status: statusResend } = useResendVerifyEmailMutation();
    const [success, setSuccess] = useState(false);
    const [resend, setResend] = useState('');

    const formik = useFormik({
        initialValues,
        validate,
        validateOnMount: true,
        isInitialValid: false,
        onSubmit: _values => {},
    });

    useEffect(() => {
        const verify = async () => {
            if (id && hash && expires && signature) {
                try {
                    const { success } = await verifyEmail({
                        id,
                        hash,
                        expires,
                        signature,
                    });
                    if (success) {
                        setSuccess(true);
                        formik.setSubmitting(false);
                    }
                } catch {
                    setSuccess(false);
                }
            }
        };
        verify();
    }, [signature, formik.setSubmitting, verifyEmail, id, hash, expires]);

    const handleResend = async () => {
        if (success) {
            navigate(paths.sign.index);
        } else if (formik.values.email) {
            try {
                const { success } = await resendVerifyEmail({
                    email: formik.values.email,
                });
                if (success) {
                    setResend(t('resend-email'));
                }
            } catch (error) {
                if (isApiError(error)) {
                    showSnackbar({
                        open: true,
                        message: error.data.message,
                        severity: snackbarColor.error,
                    });
                }
                formik.setSubmitting(false);
            }
        }
    };

    if (status === 'pending') {
        return <LoadingScreen />;
    }

    return (
        <div className="flex flex-wrap w-full justify-center h-[80%] flex-col items-center mt-[120px]">
            {resend ? (
                <Text variant="h3" color="text-primary" children={resend} />
            ) : (
                <>
                    <Text
                        variant="h3"
                        color="text-primary"
                        children={success ? t('verifyed-email') : t('verifyed-fail')}
                        className="text-center"
                    />

                    {!success ? (
                        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit} className="mt-[15px]">
                            <FormControl>
                                <Input
                                    id="email"
                                    name="email"
                                    type="text"
                                    placeholder={t('email')}
                                    inputProps={{ tabIndex: 1 }}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                    endAdornment={!formik.errors.email ? <CheckIcon color="secondary" /> : null}
                                />
                                <Box mt="40px" sx={{ '& p': { textAlign: 'center' } }}>
                                    <Text variant="body2" color="text-text-secondary" children={t('helpers.registration')} />
                                </Box>
                            </FormControl>
                        </form>
                    ) : null}
                    <div className="mt-[40px]">
                        <Button
                            disabled={(!!formik.errors.email || formik.isSubmitting) && !success}
                            className={statusResend === 'pending' ? 'p-0 [&_svg]:text-[1.3rem]' : undefined}
                            variant="contained"
                            onClick={handleResend}
                        >
                            {success ? (
                                t('buttons.entering')
                            ) : statusResend === 'pending' ? (
                                <Loading color="primary" />
                            ) : (
                                t('buttons.resend')
                            )}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
